import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  KnowledgeFilesService,
  KnowledgeReview,
  KnowledgeReviewApproval,
  KnowledgeReviewClaim,
  KnowledgeReviewClaimEdit,
  KnowledgeReviewEntity,
  KnowledgeReviewEntityEdit,
  KnowledgeReviewSection,
} from '../../services/knowledge-files.service';
import { claimTypeLabel } from '../../utils/claim-type-label.util';

/** Claims por página. Las páginas son del servidor: una propuesta grande no se carga entera. */
export const REVIEW_PAGE_SIZE = 10;
/** Cada cuánto se mira si el consumer ya escribió lo confirmado, y cuántas veces. */
export const COMMIT_POLL_MS = 2000;
export const COMMIT_POLL_MAX = 45;

const MIN_TEXT = 10;
const MAX_TEXT = 2000;
const MAX_ENTITIES = 8;

/** Mismos valores que `schemas/extraction.py` del context-graph (ClaimType, Polarity, EntityKind). */
export const CLAIM_TYPES = [
  'policy', 'authorization', 'prohibition', 'classification', 'ownership', 'membership',
  'behavior_baseline', 'event_context', 'correction',
] as const;
export const POLARITIES = ['allow', 'deny', 'inform'] as const;
export const ENTITY_KINDS = [
  'person', 'department', 'role', 'system', 'application', 'network_asset', 'data_category',
  'policy', 'behavior', 'event', 'indicator',
] as const;
export const ENTITY_ROLES = ['subject', 'object', 'context'] as const;

/** Cómo termina el diálogo, para que quien lo abrió refresque lo suyo y avise. */
export type ReviewOutcome = 'committed' | 'committing' | 'discarded';

/** Borrador de la edición de UNA claim (todo editable: texto, tipo, polaridad, confianza, entidades). */
export interface ClaimDraft {
  index: number;
  text: string;
  claimType: string;
  polarity: string;
  confidence: number;
  entities: KnowledgeReviewEntityEdit[];
}

const entityEdit = (e: KnowledgeReviewEntity): KnowledgeReviewEntityEdit => ({
  kind: e.kind,
  surface: e.surface ?? e.normalized,
  normalized: e.normalized,
  roleInClaim: e.roleInClaim ?? 'context',
});

const sameEntities = (a: KnowledgeReviewEntityEdit[], b: KnowledgeReviewEntityEdit[]): boolean =>
  a.length === b.length && a.every((x, i) =>
    x.kind === b[i].kind && x.surface === b[i].surface && x.normalized === b[i].normalized && x.roleInClaim === b[i].roleInClaim);

/**
 * Revisión de lo que salió de un fichero antes de que entre en el conocimiento (SYN-899).
 *
 * Enseña el informe de procesado (qué se leyó, qué falló, semáforo) y las claims propuestas por
 * páginas, agrupables por sección. Se puede desmarcar cualquier claim y EDITAR todo de las que se
 * quedan; confirmar manda al servidor las posiciones aprobadas y, de las editadas, lo que cambió.
 *
 * La propuesta vive en el servidor: aquí solo se guarda qué se desmarcó y qué se editó, por
 * posición global (no por página), de modo que moverse entre páginas no pierde nada.
 */
@Component({
  selector: 'app-knowledge-review-dialog',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './knowledge-review-dialog.component.html',
  styleUrls: ['./knowledge-review-dialog.component.scss'],
})
export class KnowledgeReviewDialogComponent implements OnChanges, OnDestroy {
  private readonly files = inject(KnowledgeFilesService);
  private readonly translate = inject(TranslateService);

  /** Id de la revisión (`KnowledgeFile.reviewId`). */
  @Input() reviewId: string | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() done = new EventEmitter<ReviewOutcome>();

  readonly claimTypes = CLAIM_TYPES;
  readonly polarities = POLARITIES;
  readonly entityKinds = ENTITY_KINDS;
  readonly entityRoles = ENTITY_ROLES;

  readonly review = signal<KnowledgeReview | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly section = signal<string | null>(null);

  /** Posiciones DESMARCADAS: por defecto está todo aprobado (lo habitual es aprobar el lote y
   * quitar lo puntual). Se guarda lo desmarcado, no lo marcado, para que no dependa de cuántas
   * claims se hayan cargado. */
  readonly deselected = signal<ReadonlySet<number>>(new Set());
  readonly edits = signal<ReadonlyMap<number, KnowledgeReviewClaimEdit>>(new Map());

  readonly draft = signal<ClaimDraft | null>(null);
  readonly draftError = signal<string | null>(null);

  readonly acceptErrors = signal(false);
  readonly confirmingDiscard = signal(false);
  readonly saving = signal(false);   // enviando la confirmación
  readonly writing = signal(false);  // confirmada: esperando a que el consumer la escriba
  readonly actionError = signal<string | null>(null);

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private loadSeq = 0;

  readonly total = computed(() => this.review()?.totalClaims ?? 0);
  readonly approvedCount = computed(() => Math.max(0, this.total() - this.deselected().size));
  readonly editedCount = computed(() => this.edits().size);
  readonly sectionErrors = computed(() => this.review()?.report.totals.errors ?? 0);
  /** Con secciones que fallaron, confirmar exige aceptarlo de forma explícita (RF-05). */
  readonly needsAcceptance = computed(() => this.sectionErrors() > 0);
  readonly canConfirm = computed(() =>
    !this.saving() && !this.writing() && this.draft() === null && this.approvedCount() > 0
    && (!this.needsAcceptance() || this.acceptErrors()));
  readonly busy = computed(() => this.saving() || this.writing());

  /** Las claims de la página, con lo editado por encima de lo que propuso el extractor. */
  readonly pageClaims = computed<KnowledgeReviewClaim[]>(() => {
    const edits = this.edits();
    return (this.review()?.claims ?? []).map(c => this.applyEdit(c, edits.get(c.index)));
  });

  /** Secciones con claims, para navegar y marcar por sección. */
  readonly sectionsWithClaims = computed(() =>
    (this.review()?.report.sections ?? []).filter(s => s.claimIndexes.length > 0));

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reviewId'] && this.reviewId) {
      this.deselected.set(new Set());
      this.edits.set(new Map());
      this.draft.set(null);
      this.section.set(null);
      this.acceptErrors.set(false);
      this.load(1);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.loadSeq++;
  }

  // ─── Carga y páginas ─────────────────────────────────────────────────────────────

  load(page: number): void {
    if (!this.reviewId) return;
    const seq = ++this.loadSeq;
    this.loading.set(true);
    this.loadError.set(null);
    this.files.review(this.reviewId, { page, pageSize: REVIEW_PAGE_SIZE, section: this.section() }).subscribe({
      next: r => {
        if (seq !== this.loadSeq) return; // llegó tarde: ya se pidió otra página
        this.loading.set(false);
        if (!r) {
          this.loadError.set(this.translate.instant('knowledge.review.notFound'));
          return;
        }
        this.review.set(r);
        // Un rechazo del consumer a la última confirmación: se enseña y se puede corregir.
        if (r.commitError) this.actionError.set(this.translate.instant('knowledge.review.commitRejected', { error: r.commitError }));
      },
      error: () => {
        if (seq !== this.loadSeq) return;
        this.loading.set(false);
        this.loadError.set(this.translate.instant('knowledge.review.loadError'));
      },
    });
  }

  goTo(page: number): void {
    const r = this.review();
    if (!r || this.busy() || this.draft()) return;
    this.load(Math.min(Math.max(page, 1), r.pageCount));
  }

  filterSection(section: string | null): void {
    if (this.busy() || this.draft()) return;
    this.section.set(section);
    this.load(1);
  }

  // ─── Marcar y desmarcar ──────────────────────────────────────────────────────────

  isApproved(index: number): boolean {
    return !this.deselected().has(index);
  }

  toggleClaim(index: number): void {
    const next = new Set(this.deselected());
    if (next.has(index)) next.delete(index); else next.add(index);
    this.deselected.set(next);
  }

  /** ¿Están aprobadas todas las claims de esta sección (aunque no estén en la página)? */
  isSectionApproved(s: KnowledgeReviewSection): boolean {
    return s.claimIndexes.every(i => this.isApproved(i));
  }

  /** Marca o desmarca la sección entera, con todas sus claims, estén o no cargadas. */
  toggleSection(s: KnowledgeReviewSection): void {
    const next = new Set(this.deselected());
    const all = this.isSectionApproved(s);
    for (const i of s.claimIndexes) {
      if (all) next.add(i); else next.delete(i);
    }
    this.deselected.set(next);
  }

  approveAll(): void {
    this.deselected.set(new Set());
  }

  // ─── Edición ─────────────────────────────────────────────────────────────────────

  isEdited(index: number): boolean {
    return this.edits().has(index);
  }

  startEdit(c: KnowledgeReviewClaim): void {
    if (this.busy()) return;
    this.draftError.set(null);
    this.draft.set({
      index: c.index, text: c.text, claimType: c.claimType, polarity: c.polarity,
      confidence: c.confidence, entities: c.entities.map(entityEdit),
    });
  }

  patchDraft(patch: Partial<ClaimDraft>): void {
    const d = this.draft();
    if (d) this.draft.set({ ...d, ...patch });
  }

  patchEntity(i: number, patch: Partial<KnowledgeReviewEntityEdit>): void {
    const d = this.draft();
    if (!d) return;
    this.draft.set({ ...d, entities: d.entities.map((e, k) => (k === i ? { ...e, ...patch } : e)) });
  }

  addEntity(): void {
    const d = this.draft();
    if (!d || d.entities.length >= MAX_ENTITIES) return;
    this.draft.set({ ...d, entities: [...d.entities, { kind: 'system', surface: '', normalized: '', roleInClaim: 'context' }] });
  }

  removeEntity(i: number): void {
    const d = this.draft();
    if (d) this.draft.set({ ...d, entities: d.entities.filter((_, k) => k !== i) });
  }

  cancelEdit(): void {
    this.draft.set(null);
    this.draftError.set(null);
  }

  /** Guarda el borrador como edición de la claim, o la quita si lo dejó como estaba. */
  saveEdit(): void {
    const d = this.draft();
    const original = this.originalOf(d?.index);
    if (!d || !original) return;

    const error = this.validateDraft(d);
    if (error) {
      this.draftError.set(error);
      return;
    }

    const text = d.text.split(/\s+/).filter(Boolean).join(' ');
    const entities = d.entities.map(e => ({
      kind: e.kind,
      surface: e.surface.split(/\s+/).filter(Boolean).join(' '),
      normalized: e.normalized.split(/\s+/).filter(Boolean).join(' '),
      roleInClaim: e.roleInClaim,
    }));
    // Solo lo que cambió respecto a lo propuesto: lo demás se queda como estaba en el servidor.
    const edit: KnowledgeReviewClaimEdit = {};
    if (text !== original.text) edit.text = text;
    if (d.claimType !== original.claimType) edit.claimType = d.claimType;
    if (d.polarity !== original.polarity) edit.polarity = d.polarity;
    if (d.confidence !== original.confidence) edit.confidence = d.confidence;
    if (!sameEntities(entities, original.entities.map(entityEdit))) edit.entities = entities;

    const next = new Map(this.edits());
    if (Object.keys(edit).length) next.set(d.index, edit); else next.delete(d.index);
    this.edits.set(next);
    this.cancelEdit();
  }

  /** Vuelve a lo que propuso el extractor. */
  restore(index: number): void {
    const next = new Map(this.edits());
    next.delete(index);
    this.edits.set(next);
  }

  private validateDraft(d: ClaimDraft): string | null {
    const t = this.translate;
    const text = d.text.split(/\s+/).filter(Boolean).join(' ');
    if (text.length < MIN_TEXT) return t.instant('knowledge.review.edit.errTextShort', { min: MIN_TEXT });
    if (text.length > MAX_TEXT) return t.instant('knowledge.review.edit.errTextLong', { max: MAX_TEXT });
    if (!(d.confidence >= 0.5 && d.confidence <= 1)) return t.instant('knowledge.review.edit.errConfidence');
    if (d.entities.length < 1) return t.instant('knowledge.review.edit.errNoEntities');
    if (d.entities.length > MAX_ENTITIES) return t.instant('knowledge.review.edit.errTooManyEntities', { max: MAX_ENTITIES });
    if (d.entities.some(e => e.surface.trim().length < 2 || e.normalized.trim().length < 2)) {
      return t.instant('knowledge.review.edit.errEntityName');
    }
    return null;
  }

  // ─── Confirmar y descartar ───────────────────────────────────────────────────────

  /** Lo que se manda: cada posición aprobada y, de las editadas, lo que cambió. */
  buildApproved(): KnowledgeReviewApproval[] {
    const edits = this.edits();
    const out: KnowledgeReviewApproval[] = [];
    for (let index = 0; index < this.total(); index++) {
      if (this.deselected().has(index)) continue;
      const edit = edits.get(index);
      out.push(edit ? { index, edit } : { index });
    }
    return out;
  }

  confirm(): void {
    if (!this.canConfirm() || !this.reviewId) return;
    this.actionError.set(null);
    this.saving.set(true);
    this.files.confirmReview(this.reviewId, this.buildApproved()).subscribe({
      next: res => {
        this.saving.set(false);
        if (!res.success) {
          this.actionError.set(res.error || this.translate.instant('knowledge.review.confirmError'));
          return;
        }
        this.writing.set(true);
        this.pollCommit(0);
      },
      error: () => {
        this.saving.set(false);
        this.actionError.set(this.translate.instant('knowledge.review.confirmErrorConn'));
      },
    });
  }

  /** La escritura la hace el consumer, aparte: se mira hasta que termina o lo rechaza. */
  private pollCommit(attempt: number): void {
    if (!this.reviewId) return;
    this.pollTimer = setTimeout(() => {
      this.files.review(this.reviewId!, { page: 1, pageSize: 1 }).subscribe({
        next: r => {
          if (!r || r.status === 'committed') {
            this.writing.set(false);
            this.done.emit('committed');
          } else if (r.status === 'ready' && r.commitError) {
            // El consumer rechazó lo confirmado (p. ej. una entidad que no valida): se puede corregir.
            this.writing.set(false);
            this.actionError.set(this.translate.instant('knowledge.review.commitRejected', { error: r.commitError }));
            this.load(this.review()?.page ?? 1);
          } else if (attempt + 1 >= COMMIT_POLL_MAX) {
            this.writing.set(false);
            this.done.emit('committing'); // sigue en marcha: la lista de ficheros lo irá mostrando
          } else {
            this.pollCommit(attempt + 1);
          }
        },
        error: () => {
          // Un fallo puntual de red no es un rechazo: se sigue mirando hasta agotar los intentos.
          if (attempt + 1 >= COMMIT_POLL_MAX) {
            this.writing.set(false);
            this.done.emit('committing');
          } else {
            this.pollCommit(attempt + 1);
          }
        },
      });
    }, COMMIT_POLL_MS);
  }

  askDiscard(): void {
    if (!this.busy()) this.confirmingDiscard.set(true);
  }

  cancelDiscard(): void {
    this.confirmingDiscard.set(false);
  }

  discard(): void {
    if (!this.reviewId || this.busy()) return;
    this.actionError.set(null);
    this.saving.set(true);
    this.files.discardReview(this.reviewId).subscribe({
      next: res => {
        this.saving.set(false);
        if (res.success) {
          this.done.emit('discarded');
        } else {
          this.confirmingDiscard.set(false);
          this.actionError.set(res.error || this.translate.instant('knowledge.review.discardError'));
        }
      },
      error: () => {
        this.saving.set(false);
        this.confirmingDiscard.set(false);
        this.actionError.set(this.translate.instant('knowledge.review.discardErrorConn'));
      },
    });
  }

  close(): void {
    if (this.busy()) return;
    this.closed.emit();
  }

  // ─── Presentación ────────────────────────────────────────────────────────────────

  claimTypeLabel(t: string): string {
    return claimTypeLabel(t, this.translate);
  }

  sectionLabel(s: string | null | undefined): string {
    return s || this.translate.instant('knowledge.claimReview.noSection');
  }

  entityStatus(e: KnowledgeReviewEntity): 'new' | 'existing' | null {
    return e.isNew == null ? null : e.isNew ? 'new' : 'existing';
  }

  private originalOf(index: number | undefined): KnowledgeReviewClaim | undefined {
    return (this.review()?.claims ?? []).find(c => c.index === index);
  }

  private applyEdit(c: KnowledgeReviewClaim, e: KnowledgeReviewClaimEdit | undefined): KnowledgeReviewClaim {
    if (!e) return c;
    return {
      ...c,
      text: e.text ?? c.text,
      claimType: e.claimType ?? c.claimType,
      polarity: e.polarity ?? c.polarity,
      confidence: e.confidence ?? c.confidence,
      // Las entidades editadas no tienen resolución (se vuelven a resolver al confirmar).
      entities: e.entities ? e.entities.map(x => ({ ...x, isNew: null })) : c.entities,
    };
  }

  private stopPolling(): void {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }
}
